'use client'

import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Button, HStack, Text, VStack } from '@chakra-ui/react'
import { Barcode, Check } from 'lucide-react'
import { toaster } from '@/shared'
import type { IApiResponse } from '@/shared/types/api'
import { getAxiosErrorData } from '@/shared/utils/axios-error'
import {
  useCompleteBloodCollectionMutation,
  useGenerateBarcodesMutation,
  useGetBloodCollectionByIdQuery,
  usePatchBloodCollectionAnalysesMutation,
} from '@/entities/blood-collection/api'
import { BloodCollectionAnalysesCard } from '@/entities/blood-collection/ui/BloodCollectionAnalysesCard'
import { BloodCollectionHistoryCard } from '@/entities/blood-collection/ui/BloodCollectionHistoryCard'
import { BloodCollectionRejectModal } from '@/features/blood-collection-reject'
import { BloodCollectionBarcodeModal } from '@/features/blood-collection-barcode'
import type { IBloodCollectionBarcode } from '@/entities/blood-collection/model'

interface Props {
  collectionStageId: string
  /** View only: analyses can't be changed and no action buttons are shown. */
  readOnly?: boolean
}

export function BloodCollectionDetail({
  collectionStageId,
  readOnly = false,
}: Props) {
  const { t, i18n } = useTranslation()

  const [rejectOpen, setRejectOpen] = useState(false)
  const [barcodeModalOpen, setBarcodeModalOpen] = useState(false)
  const [generatedBarcodes, setGeneratedBarcodes] = useState<
    IBloodCollectionBarcode[]
  >([])
  const [selectedAnalysisIds, setSelectedAnalysisIds] = useState<Set<string>>(
    new Set(),
  )
  const [savedAnalysisIds, setSavedAnalysisIds] = useState<Set<string>>(
    new Set(),
  )

  const {
    data: response,
    isPending,
    isError,
  } = useGetBloodCollectionByIdQuery(collectionStageId, {
    enabled: !!collectionStageId,
  })
  const detail = response?.data
  const isEditable = !readOnly && detail?.status === 'WAITING'
  const isCompleted = !readOnly && detail?.status === 'COMPLETED'

  const { mutate: completeCollection, isPending: isCompleting } =
    useCompleteBloodCollectionMutation(collectionStageId)
  const { mutate: generateBarcodes, isPending: isGeneratingBarcodes } =
    useGenerateBarcodesMutation(collectionStageId)
  const { mutate: patchAnalyses, isPending: isPatchingAnalyses } =
    usePatchBloodCollectionAnalysesMutation(collectionStageId)

  useEffect(() => {
    if (!detail?.analyses.length) return

    const ids = new Set(
      detail.analyses
        .filter((item) => item.status.toUpperCase() !== 'REMOVED')
        .map((item) => item.id),
    )
    setSelectedAnalysisIds(ids)
    setSavedAnalysisIds(new Set(ids))
  }, [detail])

  const analysisLabelKey =
    i18n.language === 'uz' ? 'analysis_name_en' : 'analysis_name_ru'

  const hasAnalysesChanges =
    selectedAnalysisIds.size !== savedAnalysisIds.size ||
    [...selectedAnalysisIds].some((id) => !savedAnalysisIds.has(id))

  const notifyApiError = (error: unknown, fallbackKey: string) => {
    const apiErrors = getAxiosErrorData<IApiResponse<null>>(error)?.errors
    if (apiErrors?.[0]?.message) {
      apiErrors.forEach((apiError) =>
        toaster.error({ title: apiError.message }),
      )
      return
    }
    toaster.error({ title: t(fallbackKey) })
  }

  const handleAnalysisToggle = (analysisId: string, checked: boolean) => {
    setSelectedAnalysisIds((previous) => {
      const next = new Set(previous)
      if (checked) {
        next.add(analysisId)
      } else {
        next.delete(analysisId)
      }
      return next
    })
  }

  const handleSaveAnalyses = () => {
    const allAnalysisIds = detail?.analyses.map((item) => item.id) ?? []

    patchAnalyses(
      {
        selected_analysis_ids: allAnalysisIds.filter((id) =>
          selectedAnalysisIds.has(id),
        ),
        removed_analysis_ids: allAnalysisIds.filter(
          (id) => !selectedAnalysisIds.has(id),
        ),
        manual_analysis_template_ids: [],
      },
      {
        onSuccess: () => {
          setSavedAnalysisIds(new Set(selectedAnalysisIds))
        },
        onError: (error) =>
          notifyApiError(
            error,
            'laboratory_research.blood_collection.analyses_error',
          ),
      },
    )
  }

  const handleComplete = () => {
    completeCollection(undefined, {
      onSuccess: () => {
        toaster.success({
          title: t('laboratory_research.blood_collection.complete_success'),
        })
      },
      onError: (error) =>
        notifyApiError(
          error,
          'laboratory_research.blood_collection.complete_error',
        ),
    })
  }

  const handleGenerateBarcodes = () => {
    const selected = [...selectedAnalysisIds]
    if (selected.length === 0) {
      toaster.error({
        title: t('laboratory_research.blood_collection.barcodes_no_analyses'),
      })
      return
    }

    generateBarcodes(
      { selected_analysis_ids: selected },
      {
        onSuccess: (result) => {
          const barcodes = result.data
          if (!barcodes.length) {
            toaster.error({
              title: t('laboratory_research.blood_collection.barcodes_error'),
            })
            return
          }
          setGeneratedBarcodes(barcodes)
          setBarcodeModalOpen(true)
        },
        onError: (error) =>
          notifyApiError(
            error,
            'laboratory_research.blood_collection.barcodes_error',
          ),
      },
    )
  }

  if (isError) {
    return (
      <Box p={6}>
        <Text color="fg.muted">
          {t('laboratory_research.blood_collection.load_error')}
        </Text>
      </Box>
    )
  }

  return (
    <VStack gap={5} align="stretch">
      <BloodCollectionAnalysesCard
        analyses={detail?.analyses || []}
        selectedAnalysisIds={selectedAnalysisIds}
        onAnalysisToggle={handleAnalysisToggle}
        disabled={isPatchingAnalyses || !isEditable}
        isLoading={isPending}
        analysisLabelKey={analysisLabelKey}
        hasChanges={isEditable && hasAnalysesChanges}
        isSaving={isPatchingAnalyses}
        onSave={handleSaveAnalyses}
      />

      <BloodCollectionHistoryCard
        history={detail?.history}
        isLoading={isPending}
      />

      <HStack justify="flex-end" gap={3}>
        {isEditable && (
          <Button
            bg="primary.solid"
            color="white"
            borderRadius="md"
            size="sm"
            px={6}
            gap={2}
            disabled={isPending || isCompleting}
            loading={isCompleting}
            onClick={handleComplete}
            _hover={{ opacity: 0.85 }}
            _active={{ opacity: 0.7 }}
          >
            <Check size={16} />
            {t('laboratory_research.blood_collection.complete')}
          </Button>
        )}

        {isCompleted && (
          <Button
            variant="outline"
            borderRadius="md"
            size="sm"
            px={6}
            gap={2}
            disabled={isPending || isGeneratingBarcodes}
            loading={isGeneratingBarcodes}
            onClick={handleGenerateBarcodes}
          >
            <Barcode size={16} />
            {t('laboratory_research.blood_collection.generate_barcodes')}
          </Button>
        )}

        {isEditable && (
          <Button
            variant="outline"
            color="primary.fg"
            borderColor="primary.fg"
            borderRadius="md"
            size="sm"
            px={6}
            disabled={isPending}
            onClick={() => setRejectOpen(true)}
            _hover={{ opacity: 0.85 }}
            _active={{ opacity: 0.7 }}
          >
            {t('laboratory_research.blood_collection.reject')}
          </Button>
        )}
      </HStack>

      <BloodCollectionRejectModal
        open={rejectOpen}
        collectionStageId={collectionStageId}
        onClose={() => setRejectOpen(false)}
      />

      <BloodCollectionBarcodeModal
        open={barcodeModalOpen}
        collectionStageId={collectionStageId}
        barcodes={generatedBarcodes}
        detail={detail}
        onClose={() => setBarcodeModalOpen(false)}
      />
    </VStack>
  )
}
